import React from 'react';
import { AlertTriangle } from 'lucide-react';
import ModalPortal from './ModalPortal';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Stable label for the confirm button (e.g. "delete") for e2e and accessibility */
  confirmAriaLabel?: string;
  /** 'danger' for destructive actions (red confirm button), 'primary' otherwise */
  variant?: 'danger' | 'primary';
  /** Show a loading state on the confirm button while async onConfirm runs */
  isConfirming?: boolean;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmAriaLabel,
  variant = 'primary',
  isConfirming = false,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  const isDanger = variant === 'danger';

  return (
    <ModalPortal onClose={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                isDanger ? 'bg-red-100' : 'bg-primary/10'
              }`}
            >
              <AlertTriangle
                className={isDanger ? 'h-6 w-6 text-red-600' : 'h-6 w-6 text-primary'}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm text-gray-600">{message}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isConfirming}
              className="px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isConfirming}
              aria-label={confirmAriaLabel ?? confirmLabel}
              className={`px-4 py-2.5 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 ${
                isDanger
                  ? 'bg-red-600 text-white hover:bg-red-700 border border-red-600'
                  : 'bg-primary text-white hover:bg-primary/90 border border-primary'
              }`}
            >
              {isConfirming ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Please wait...</span>
                </>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
